import { NotificationService, useIntl } from '@capsicum/common';
import { Snackbar } from '@capsicum/common-web';
import { useBrowserTransaction } from '@socios-blk/browser';
import { useEffect } from 'react';

type UnstakeStakingContractParams = {
	amount: string;
	navigateToPartnerDetailsPage?: () => void;
	stakingContractAddress?: string;
	tokenContractAddress?: string;
};

export const useUnstakeStakingContract = (params: UnstakeStakingContractParams) => {
	const { $t } = useIntl();
	const { estimateGasFee, gasFeeEstimate, isError, isPending, sendTransaction, TRANSACTIONS, userOpStatus } =
		useBrowserTransaction();

	const isButtonDisabled = isPending || !!userOpStatus?.transactionHash;

	const onErrorCallback = () => {
		return NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('survey.toast.error.topText')}
					testID="surveyUnstake-error-notification"
					variant="error"
				/>
			)
		});
	};

	const onSuccessCallback = () => {
		params.navigateToPartnerDetailsPage?.();

		return NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('survey.toast.pending.bottomText')}
					testID="surveyUnstake-pending-notification"
					title={$t('survey.toast.pending.topTextUnstake')}
					variant="pending"
				/>
			)
		});
	};

	const transaction = Object.values(params).every(Boolean)
		? [
				TRANSACTIONS.UNSTAKE({
					amount: params.amount,
					stakingContractAddress: params.stakingContractAddress!,
					tokenContractAddress: params.tokenContractAddress!
				})
			]
		: undefined;

	useEffect(() => {
		if (gasFeeEstimate || !transaction || isPending || isError) {
			return;
		}

		estimateGasFee({
			onError: () => {
				onErrorCallback();
			},
			transaction
		});
	}, [gasFeeEstimate, transaction?.length, isPending, isError]);

	const sendUnstakeTransaction = async () => {
		if (!transaction) {
			onErrorCallback();

			return;
		}

		await sendTransaction({
			onError: () => {
				onErrorCallback();
			},
			onSuccess: () => {
				onSuccessCallback();
			},
			transaction
		});
	};

	return {
		gasFeeEstimate,
		isButtonDisabled,
		isError,
		isTransferBlocked: isPending,
		isTransferPending: isPending,
		sendUnstakeTransaction,
		userOpStatus
	};
};
